import type { ReactElement } from "react";
import {
  siApple,
  siFacebook,
  siGoogle,
  siGoogleanalytics,
  siGooglecalendar,
  siGooglemeet,
  siMailchimp,
  siMailgun,
  siMeta,
  siMercadopago,
  siPaypal,
  siRazorpay,
  siSquare,
  siStripe,
  siWhatsapp,
  siWoocommerce,
  siWordpress,
  siZoom,
  type SimpleIcon
} from "simple-icons";

type LogoSize = "small" | "medium" | "large";

const PROVIDER_ICONS: Partial<Record<string, SimpleIcon>> = {
  mercadopago: siMercadopago,
  "google-calendar": siGooglecalendar,
  "google-meet": siGooglemeet,
  "google-oauth": siGoogle,
  "outlook-calendar": undefined,
  "outlook-mail": undefined,
  "apple-caldav": siApple,
  zoom: siZoom,
  lessonspace: undefined,
  stripe: siStripe,
  paypal: siPaypal,
  square: siSquare,
  mollie: undefined,
  razorpay: siRazorpay,
  barion: undefined,
  woocommerce: siWoocommerce,
  wordpress: siWordpress,
  mailchimp: siMailchimp,
  "facebook-oauth": siFacebook,
  recaptcha: siGoogle,
  analytics: siGoogleanalytics,
  pixel: siMeta,
  smtp: undefined,
  mailgun: siMailgun,
  sms: undefined,
  whatsapp: siWhatsapp,
  webhooks: undefined
};

const PROVIDER_LABELS: Record<string, string> = {
  mercadopago: "Mercado Pago",
  "google-calendar": "Google Calendar",
  "google-meet": "Google Meet",
  "google-oauth": "Google OAuth",
  "outlook-calendar": "Outlook Calendar",
  "outlook-mail": "Outlook Mail",
  "apple-caldav": "Apple Calendar",
  zoom: "Zoom",
  lessonspace: "Lessonspace",
  stripe: "Stripe",
  paypal: "PayPal",
  square: "Square",
  mollie: "Mollie",
  razorpay: "Razorpay",
  barion: "Barion",
  woocommerce: "WooCommerce",
  wordpress: "WordPress",
  mailchimp: "Mailchimp",
  "facebook-oauth": "Facebook OAuth",
  recaptcha: "reCAPTCHA",
  analytics: "Google Analytics",
  pixel: "Meta Pixel",
  smtp: "SMTP",
  mailgun: "Mailgun",
  sms: "SMS vía Twilio",
  whatsapp: "WhatsApp",
  webhooks: "Webhooks"
};

function normalizeProvider(provider: string): string {
  return provider.trim().toLowerCase();
}

export function providerLabel(provider: string, fallback?: string): string {
  const normalized = normalizeProvider(provider);
  return PROVIDER_LABELS[normalized] ?? fallback ?? provider;
}

export function providerHasOfficialIcon(provider: string): boolean {
  return Boolean(PROVIDER_ICONS[normalizeProvider(provider)]);
}

export function KurooMark({ size = "medium", className = "" }: { size?: LogoSize; className?: string }): ReactElement {
  return <span className={`brand-icon brand-icon-kuroo brand-icon-${size} ${className}`.trim()} aria-hidden="true">K</span>;
}

export function ProviderLogo({ provider, size = "medium", className = "", decorative = true }: { provider: string; size?: LogoSize; className?: string; decorative?: boolean }): ReactElement {
  const normalized = normalizeProvider(provider);
  const icon = PROVIDER_ICONS[normalized];
  const label = providerLabel(provider);
  const props = decorative ? { "aria-hidden": true as const } : { role: "img" as const, "aria-label": label };

  return <span className={`brand-icon brand-icon-provider brand-icon-${size} ${className}`.trim()} title={label} {...props}>
    {icon ? <svg viewBox="0 0 24 24" focusable="false" style={{ color: `#${icon.hex}` }}><path d={icon.path} /></svg> : <KurooMark size={size} />}
  </span>;
}

export function IntegrationBadge({ provider, label, size = "small" }: { provider: string; label: string; size?: LogoSize }): ReactElement {
  return <span className="integration-brand-badge"><ProviderLogo provider={provider} size={size} /><span>{label}</span></span>;
}

export function KurooServiceIcon({ size = "medium", className = "" }: { size?: LogoSize; className?: string }): ReactElement {
  return <KurooMark size={size} className={className} />;
}

export { PROVIDER_ICONS };
